import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { ArrowLeft, Languages, Link2, ShieldAlert, Trash2, X } from 'lucide-react';
import type { Directory, StrategyNodeList, Task, TaskCreate, TaskStatus, TaskTopicList, TaskUpdate, TranslateResponse } from '@mnc/contracts';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { describeError } from '../../lib/errors';
import { useParameterOptions } from '../../lib/parameters';
import { pick } from '../../i18n';
import { Badge, ConfirmDialog, Field, PageHeader, SearchableSelect, TableSkeleton } from '../../components/ui';
import { AttachmentsPanel, NotesThread, TeamPicker, UserPicker, VisibilityControl } from '../../components/records';
import { statusTone } from './TasksPage';

const STATUSES: TaskStatus[] = ['todo', 'in_progress', 'blocked', 'done', 'cancelled'];

type Form = { title: string; description: string; status: TaskStatus; pillar_code: string; urgency_code: string; follow_up_with_code: string; topic_id: string; start_date: string; due_date: string; owner_id: string; strategy_node_id: string; team: string[]; onlyMe: boolean };
const empty: Form = { title: '', description: '', status: 'todo', pillar_code: '', urgency_code: '', follow_up_with_code: '', topic_id: '', start_date: '', due_date: '', owner_id: '', strategy_node_id: '', team: [], onlyMe: false };

/** Full-page task editor (MRLCO §7.8): create at /tasks/new, edit at /tasks/:id with the shared panels beside the form. */
export function TaskEditPage() {
  const { t, i18n } = useTranslation();
  const { id } = useParams({ strict: false }) as { id?: string };
  const isNew = !id;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { can, config, me } = useAuth();
  const presetNode = (useSearch({ strict: false }) as { strategy_node?: string }).strategy_node;
  const strategyOn = Boolean(config?.enabled_modules.includes('strategy')) && can('strategy');
  const leaves = useQuery({ queryKey: ['strategy', 'leaves'], queryFn: () => api.get<StrategyNodeList>('/api/strategy/nodes?leaf_only=true&page_size=200&sort=code'), enabled: strategyOn, staleTime: 60_000 });
  const task = useQuery({ queryKey: ['task', id], queryFn: () => api.get<Task>(`/api/tasks/${id}`), enabled: !isNew });
  const topics = useQuery({ queryKey: ['task-topics'], queryFn: () => api.get<TaskTopicList>('/api/tasks/topics'), staleTime: 60_000 });
  const directory = useQuery({ queryKey: ['users', 'directory'], queryFn: () => api.get<Directory>('/api/users/directory'), staleTime: 60_000 });
  const pillar = useParameterOptions('pillar');
  const urgency = useParameterOptions('urgency');
  const followUp = useParameterOptions('follow_up_with');
  const [form, setForm] = useState<Form>(empty);
  const [translation, setTranslation] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const aiEnabled = Boolean(config?.flags.ai_translate);

  useEffect(() => {
    if (isNew) {
      if (presetNode) setForm((f) => ({ ...f, strategy_node_id: presetNode }));
      return;
    }
    if (!task.data) return;
    const d = task.data;
    setForm({ title: d.title, description: d.description ?? '', status: d.status, pillar_code: d.pillar_code ?? '', urgency_code: d.urgency_code ?? '', follow_up_with_code: d.follow_up_with_code ?? '', topic_id: d.topic_id ?? '', start_date: d.start_date ?? '', due_date: d.due_date ?? '', owner_id: d.owner?.id ?? '', strategy_node_id: d.strategy_node_id ?? '', team: d.team.map((p) => p.id), onlyMe: d.visibility === 'private' });
  }, [task.data, isNew, presetNode]);

  const topicOptions = useMemo(() => (topics.data?.items ?? []).map((tp) => ({ value: tp.id, label: pick(i18n.language, tp.name_en, tp.name_ar) })), [topics.data, i18n.language]);
  const ownerOptions = useMemo(() => (directory.data?.items ?? []).filter((u) => u.is_active).map((u) => ({ value: u.id, label: pick(i18n.language, u.display_name_en, u.display_name_ar) })), [directory.data, i18n.language]);
  const nodeOptions = useMemo(() => (leaves.data?.items ?? []).map((n) => ({ value: n.id, label: `${n.code} - ${pick(i18n.language, n.name_en, n.name_ar)}` })), [leaves.data, i18n.language]);
  const confidentialPillar = Boolean(form.pillar_code && pillar.options.find((o) => o.value === form.pillar_code)?.flags.is_confidential);

  const invalidate = () => { void qc.invalidateQueries({ queryKey: ['tasks'] }); void qc.invalidateQueries({ queryKey: ['task', id] }); };
  const create = useMutation({
    mutationFn: (b: TaskCreate) => api.post<Task>('/api/tasks', b),
    onSuccess: (created) => { toast.success(t('tasks.saved')); invalidate(); void navigate({ to: '/tasks/$id', params: { id: created.id }, replace: true }); },
    onError: (e) => toast.error(describeError(e, t)),
  });
  const update = useMutation({
    mutationFn: (b: TaskUpdate) => api.patch<Task>(`/api/tasks/${id}`, b),
    onSuccess: () => { toast.success(t('tasks.saved')); invalidate(); },
    onError: (e) => toast.error(describeError(e, t)),
  });
  const remove = useMutation({
    mutationFn: () => api.delete(`/api/tasks/${id}`),
    onSuccess: () => { toast.success(t('tasks.deleted')); void qc.invalidateQueries({ queryKey: ['tasks'] }); void navigate({ to: '/tasks' }); },
    onError: (e) => toast.error(describeError(e, t)),
  });
  const translate = useMutation({
    mutationFn: () => api.post<TranslateResponse>('/api/translate', { text: form.description, from: i18n.language === 'ar' ? 'ar' : 'en', to: i18n.language === 'ar' ? 'en' : 'ar' }),
    onSuccess: (r) => setTranslation(r.suggestion),
    onError: (e) => toast.error(describeError(e, t)),
  });

  const submit = () => {
    const base = {
      title: form.title,
      description: form.description || null,
      status: form.status,
      pillar_code: form.pillar_code || null,
      urgency_code: form.urgency_code || null,
      follow_up_with_code: form.follow_up_with_code || null,
      topic_id: form.topic_id || null,
      start_date: form.start_date || null,
      due_date: form.due_date || null,
      owner_id: form.owner_id || null,
      strategy_node_id: form.strategy_node_id || null,
    };
    if (isNew) create.mutate({ ...base, team: form.team, links: [], ...(form.onlyMe ? { visibility: 'private' as const } : {}) });
    else update.mutate(base);
  };
  const removeLink = (target_type: string, target_id: string) => {
    if (!task.data) return;
    update.mutate({ links: task.data.links.filter((l) => !(l.target_type === target_type && l.target_id === target_id)).map((l) => ({ target_type: l.target_type, target_id: l.target_id })) });
  };

  if (!isNew && task.isLoading) return <TableSkeleton />;
  if (!isNew && !task.data) return <div className="card p-10 text-center text-sm text-neutral-500">{t('errors.notFound')}</div>;
  const editable = isNew ? can('tasks', 'can_create') : can('tasks', 'can_edit') || task.data?.created_by === me?.id;
  const canDelete = !isNew && (can('tasks', 'can_delete') || task.data?.created_by === me?.id);

  return (
    <div>
      <PageHeader
        title={isNew ? t('tasks.new') : `#${task.data!.seq} · ${task.data!.title}`}
        subtitle={!isNew && task.data ? `${t(`tasks.statuses.${task.data.status}`)}${task.data.owner ? ` · ${pick(i18n.language, task.data.owner.display_name_en, task.data.owner.display_name_ar)}` : ''}` : undefined}
        actions={
          <>
            <button type="button" className="btn-secondary" onClick={() => void navigate({ to: '/tasks' })}><ArrowLeft size={16} className="rtl:rotate-180" /> {t('app.back')}</button>
            {canDelete && <button type="button" className="btn-danger" onClick={() => setConfirmDelete(true)}><Trash2 size={16} /> {t('app.delete')}</button>}
            {editable && <button type="button" className="btn-primary" disabled={!form.title.trim() || create.isPending || update.isPending} onClick={submit}>{t('app.save')}</button>}
          </>
        }
      />
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="card grid gap-3 p-4">
          <Field label={t('tasks.title')} required><input className="input" dir="auto" value={form.title} disabled={!editable} onChange={(e) => setForm({ ...form, title: e.target.value })} /></Field>
          <Field label={t('tasks.description')}>
            <textarea className="input min-h-32" dir="auto" value={form.description} disabled={!editable} onChange={(e) => { setForm({ ...form, description: e.target.value }); setTranslation(null); }} />
            {aiEnabled && form.description && (
              <div className="mt-1">
                <button type="button" className="btn-secondary px-2 py-1 text-xs" disabled={translate.isPending} onClick={() => (translation ? setTranslation(null) : translate.mutate())}><Languages size={12} /> {translation ? t('tasks.showOriginal') : t('tasks.translate')}</button>
                {translation && <p className="mt-2 rounded-lg bg-neutral-100 p-2 text-sm dark:bg-neutral-800" dir="auto">{translation}</p>}
              </div>
            )}
          </Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t('tasks.status')}><SearchableSelect disabled={!editable} value={form.status} onChange={(v) => setForm({ ...form, status: v as TaskStatus })} options={STATUSES.map((s) => ({ value: s, label: t(`tasks.statuses.${s}`) }))} /></Field>
            <Field label={t('tasks.dueDate')}><input className="input" type="date" dir="ltr" value={form.due_date} disabled={!editable} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></Field>
            <Field label={t('tasks.startDate')}><input className="input" type="date" dir="ltr" value={form.start_date} disabled={!editable} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></Field>
            {strategyOn && <Field label={t('tasks.strategyNode')} hint={t('tasks.strategyNodeHint')}><SearchableSelect disabled={!editable} value={form.strategy_node_id} onChange={(v) => setForm({ ...form, strategy_node_id: v as string })} options={[{ value: '', label: t('tasks.noNode') }, ...nodeOptions]} /></Field>}
            <Field label={t('tasks.owner')}><SearchableSelect disabled={!editable} value={form.owner_id} onChange={(v) => setForm({ ...form, owner_id: v as string })} options={[{ value: '', label: t('app.none') }, ...ownerOptions]} /></Field>
            <Field label={t('tasks.urgency')}><SearchableSelect disabled={!editable} value={form.urgency_code} onChange={(v) => setForm({ ...form, urgency_code: v as string })} options={[{ value: '', label: t('app.none') }, ...urgency.options]} /></Field>
            <Field label={t('tasks.pillar')} hint={confidentialPillar ? t('tasks.confidentialHint') : undefined}><SearchableSelect disabled={!editable} value={form.pillar_code} onChange={(v) => setForm({ ...form, pillar_code: v as string })} options={[{ value: '', label: t('app.none') }, ...pillar.options.map((o) => ({ value: o.value, label: o.flags.is_confidential ? `${o.label} — ${t('tasks.confidential')}` : o.label }))]} /></Field>
            <Field label={t('tasks.topic')}><SearchableSelect disabled={!editable} value={form.topic_id} onChange={(v) => setForm({ ...form, topic_id: v as string })} options={[{ value: '', label: t('tasks.noTopic') }, ...topicOptions]} /></Field>
            {followUp.options.length > 0 && <Field label={t('tasks.followUp')}><SearchableSelect disabled={!editable} value={form.follow_up_with_code} onChange={(v) => setForm({ ...form, follow_up_with_code: v as string })} options={[{ value: '', label: t('app.none') }, ...followUp.options]} /></Field>}
          </div>
          {isNew && (
            <>
              <Field label={t('tasks.team')}><UserPicker value={form.team} onChange={(team) => setForm({ ...form, team })} /></Field>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.onlyMe} onChange={(e) => setForm({ ...form, onlyMe: e.target.checked })} />{t('records.onlyMe')} <span className="text-xs text-neutral-500">{t('tasks.onlyMeHint')}</span></label>
            </>
          )}
          {!isNew && task.data && task.data.links.length > 0 && (
            <div>
              <div className="label"><Link2 size={12} className="me-1 inline" />{t('tasks.links')}</div>
              <div className="flex flex-wrap gap-1">
                {task.data.links.map((l) => (
                  <Badge key={`${l.target_type}:${l.target_id}`} tone="info">
                    <span className="me-1 text-neutral-500">{l.target_type}</span>{pick(i18n.language, l.label_en, l.label_ar) || l.target_id.slice(0, 8)}
                    {editable && <button type="button" className="ms-1" onClick={() => removeLink(l.target_type, l.target_id)} aria-label={t('app.delete')}><X size={10} /></button>}
                  </Badge>
                ))}
              </div>
            </div>
          )}
          {!isNew && task.data && (
            <div className="flex flex-wrap items-center gap-2 text-xs text-neutral-500">
              <Badge tone={statusTone(task.data.status)}>{t(`tasks.statuses.${task.data.status}`)}</Badge>
              {task.data.is_confidential && <Badge tone="danger"><ShieldAlert size={10} /> {t('tasks.confidential')}</Badge>}
              <span>{t('tasks.createdAt', { date: new Date(task.data.created_at).toLocaleString(i18n.language) })}</span>
              {task.data.completed_at && <span>· {t('tasks.completedAt', { date: new Date(task.data.completed_at).toLocaleString(i18n.language) })}</span>}
            </div>
          )}
        </div>
        {!isNew && task.data && (
          <div className="grid grid-cols-1 content-start gap-4">
            <VisibilityControl ownerType="task" ownerId={task.data.id} />
            <TeamPicker ownerType="task" ownerId={task.data.id} canEdit={editable} title={t('tasks.team')} />
            <AttachmentsPanel ownerType="task" ownerId={task.data.id} canEdit={editable} />
            <NotesThread ownerType="task" ownerId={task.data.id} canModerate={can('tasks', 'can_delete')} />
          </div>
        )}
      </div>
      <ConfirmDialog open={confirmDelete} title={t('tasks.deleteTitle')} body={task.data?.title} danger onCancel={() => setConfirmDelete(false)} onConfirm={() => { setConfirmDelete(false); remove.mutate(); }} />
    </div>
  );
}
